import axios from 'axios';

// Use main API backend - Indian routes are at /api/v1/indian/*
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance for Indian API
const indianClient = axios.create({
  baseURL: `${API_URL}/api/v1/indian`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - add auth token (same auth system)
indianClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh (same as main client)
indianClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retrying, attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await axios.post(`${API_URL}/api/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const { access_token, refresh_token } = response.data;
        localStorage.setItem('access_token', access_token);
        if (refresh_token) {
          localStorage.setItem('refresh_token', refresh_token);
        }

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return indianClient(originalRequest);
      } catch (refreshError) {
        // Clear tokens and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Rebalance sweeps every account's positions at the broker, one proxy hop each, so
// it takes far longer than a normal API call: ~45-65s for 89 accounts. The
// client-wide 30s timeout aborted it every time ("Failed to check rebalance"),
// which is why rebalance appeared broken. Override per call, not globally - 30s is
// still the right default for every other endpoint.
const REBALANCE_TIMEOUT = 180000;

export const indianApi = {
  // ============= Account Management =============

  // Get all accounts
  getAccounts: async () => {
    const response = await indianClient.get('/accounts');
    return response.data;
  },

  // Add new account
  addAccount: async (data) => {
    const response = await indianClient.post('/accounts', data);
    return response.data;
  },

  // Update account
  updateAccount: async (accountId, data) => {
    const response = await indianClient.patch(`/accounts/${accountId}`, data);
    return response.data;
  },

  // Fetch ONE account's decrypted credentials on demand (edit modal only).
  // The list endpoint no longer ships secrets, so prefill the edit form from here.
  getAccountCredentials: async (accountId) => {
    const response = await indianClient.get(`/accounts/${accountId}/credentials`);
    return response.data;
  },

  // Delete account
  deleteAccount: async (accountId) => {
    const response = await indianClient.delete(`/accounts/${accountId}`);
    return response.data;
  },

  // Login single account
  loginAccount: async (accountId) => {
    const response = await indianClient.post(`/accounts/${accountId}/login`);
    return response.data;
  },

  // Login all accounts
  loginAllAccounts: async () => {
    const response = await indianClient.post('/accounts/login-all');
    return response.data;
  },

  // Login only failed accounts (retry)
  loginFailedAccounts: async () => {
    const response = await indianClient.post('/accounts/login-failed');
    return response.data;
  },

  // Login selected accounts by IDs
  loginSelectedAccounts: async (accountIds) => {
    const response = await indianClient.post('/accounts/login-selected', { account_ids: accountIds });
    return response.data;
  },

  // Disconnect single account
  disconnectAccount: async (accountId) => {
    const response = await indianClient.post(`/accounts/${accountId}/disconnect`);
    return response.data;
  },

  // Disconnect all accounts
  disconnectAllAccounts: async () => {
    const response = await indianClient.post('/accounts/disconnect-all');
    return response.data;
  },

  // Get account funds/margins
  getAccountFunds: async (accountId) => {
    const response = await indianClient.get(`/accounts/${accountId}/funds`);
    return response.data;
  },

  // ============= Copy Trading Control =============

  // Start copy trading for selected accounts
  startCopy: async (accountIds) => {
    const response = await indianClient.post('/copy/start', { account_ids: accountIds });
    return response.data;
  },

  // Stop copy trading for selected accounts
  stopCopy: async (accountIds) => {
    const response = await indianClient.post('/copy/stop', { account_ids: accountIds });
    return response.data;
  },

  // Get copy trading status
  getCopyStatus: async () => {
    const response = await indianClient.get('/copy/status');
    return response.data;
  },

  // Start copy for a single account
  startCopySingle: async (accountId) => {
    const response = await indianClient.post(`/copy/start/${accountId}`);
    return response.data;
  },

  // Stop copy for a single account
  stopCopySingle: async (accountId) => {
    const response = await indianClient.post(`/copy/stop/${accountId}`);
    return response.data;
  },

  // ============= Positions =============

  // Get positions (view=account or view=symbol)
  getPositions: async (view = 'account') => {
    const response = await indianClient.get(`/positions?view=${view}`);
    return response.data;
  },

  // Sync positions from broker
  syncPositions: async (accountIds) => {
    const response = await indianClient.post('/positions/sync', { account_ids: accountIds });
    return response.data;
  },

  // Exit positions
  exitPositions: async (accountIds, symbols = null) => {
    const response = await indianClient.post('/positions/exit', {
      account_ids: accountIds,
      symbols: symbols
    });
    return response.data;
  },

  // Exit only COPIED positions (not independent)
  exitCopiedPositions: async (accountIds) => {
    const response = await indianClient.post('/positions/exit-copied', {
      account_ids: accountIds
    });
    return response.data;
  },

  // ============= Orders =============

  // Get all orders (filter by copied=true for copied orders only)
  getOrders: async (copied = null) => {
    const params = copied !== null ? `?copied=${copied}` : '';
    const response = await indianClient.get(`/orders${params}`);
    return response.data;
  },

  // Sync orders from broker
  syncOrders: async (accountIds) => {
    const response = await indianClient.post('/orders/sync', { account_ids: accountIds });
    return response.data;
  },

  // Cancel pending orders
  cancelOrders: async (orderIds) => {
    const response = await indianClient.post('/orders/cancel', { order_ids: orderIds });
    return response.data;
  },

  // ============= Holdings =============

  // Get holdings
  getHoldings: async () => {
    const response = await indianClient.get('/holdings');
    return response.data;
  },

  // Sync holdings from broker
  syncHoldings: async (accountIds = null) => {
    const response = await indianClient.post('/holdings/sync', accountIds ? { account_ids: accountIds } : {});
    return response.data;
  },

  // Get combined holdings summary
  getHoldingsSummary: async () => {
    const response = await indianClient.get('/holdings/summary');
    return response.data;
  },

  // ============= Logs =============

  // Get logs
  getLogs: async (limit = 100) => {
    const response = await indianClient.get(`/logs?limit=${limit}`);
    return response.data;
  },

  // Add log entry
  addLog: async (logEntry) => {
    const response = await indianClient.post('/logs', logEntry);
    return response.data;
  },

  // Clear logs
  clearLogs: async () => {
    const response = await indianClient.delete('/logs');
    return response.data;
  },

  // ============= Dashboard =============

  // Get dashboard summary
  getDashboard: async () => {
    const response = await indianClient.get('/dashboard');
    return response.data;
  },

  // ============= Settings =============

  // Get copy settings
  getSettings: async () => {
    const response = await indianClient.get('/settings');
    return response.data;
  },

  // Update copy settings
  updateSettings: async (settings) => {
    const response = await indianClient.patch('/settings', settings);
    return response.data;
  },

  // ============= Index Prices =============

  // Get live index prices
  getIndices: async () => {
    const response = await indianClient.get('/indices');
    return response.data;
  },

  // ============= Legacy Endpoints (for backward compatibility) =============

  getBrokerAccounts: async () => {
    const response = await indianClient.get('/accounts');
    return response.data;
  },

  connectBroker: async (broker, credentials) => {
    const response = await indianClient.post('/accounts', {
      broker,
      ...credentials,
    });
    return response.data;
  },

  disconnectBroker: async (accountId) => {
    const response = await indianClient.delete(`/accounts/${accountId}`);
    return response.data;
  },

  setMasterAccount: async (accountId, isMaster = true) => {
    const response = await indianClient.patch(`/accounts/${accountId}`, { is_master: isMaster });
    return response.data;
  },

  updateBrokerAccount: async (accountId, data) => {
    const response = await indianClient.patch(`/accounts/${accountId}`, data);
    return response.data;
  },

  getBrokerFunds: async (accountId) => {
    const response = await indianClient.get(`/accounts/${accountId}/funds`);
    return response.data;
  },

  getBrokerPositions: async (accountId) => {
    const response = await indianClient.get(`/positions?account_id=${accountId}`);
    return response.data;
  },

  getBrokerOrders: async (accountId) => {
    const response = await indianClient.get(`/orders?account_id=${accountId}`);
    return response.data;
  },

  getCopyRelationships: async () => {
    // Copy relationships are now derived from accounts
    const accounts = await indianClient.get('/accounts');
    return accounts.data.filter(a => !a.is_master && a.copy_status !== 'stopped');
  },

  // ============= Rebalance =============

  // Check for position imbalances
  checkRebalance: async () => {
    const response = await indianClient.get('/rebalance/check', {
      timeout: REBALANCE_TIMEOUT,
    });
    return response.data;
  },

  // Execute rebalance orders
  executeRebalance: async (imbalances) => {
    const response = await indianClient.post('/rebalance/execute', { imbalances }, {
      timeout: REBALANCE_TIMEOUT,
    });
    return response.data;
  },

  // Cancel limit orders left sitting unfilled. Places NO orders - it only clears
  // stuck ones. While a stuck order is working the check nets it off and execute
  // skips the symbol, so the account can never be corrected until it is cleared.
  cancelStaleOrders: async () => {
    const response = await indianClient.post('/rebalance/cancel-stale', {}, {
      timeout: REBALANCE_TIMEOUT,
    });
    return response.data;
  },

  // ============= Performance =============

  // Headline metrics + the period series. granularity: 'day' | 'week' | 'month'.
  // Weeks run Wed->Tue and are named by the Tuesday that closes them; months are
  // the last Tuesday. The backend owns that calendar - never re-derive it here.
  getPerformanceSummary: async ({ granularity = 'week', from, to } = {}) => {
    const response = await indianClient.get('/performance/summary', {
      params: { granularity, from_date: from || undefined, to_date: to || undefined },
      timeout: 60000,
    });
    return response.data;
  },

  // Master track record, imported from Firstock 'Absolute P&L' statements.
  // Buckets by contract EXPIRY - the statement has no trade date, so a daily
  // series does not exist for this history. granularity: expiry|week|month|year
  getMasterPerformance: async ({ granularity = 'week', from, to, expiryType, optionType } = {}) => {
    const response = await indianClient.get('/performance/master', {
      params: {
        granularity,
        from_date: from || undefined,
        to_date: to || undefined,
        expiry_type: expiryType || undefined,
        option_type: optionType || undefined,
      },
      timeout: 60000,
    });
    return response.data;
  },

  // The underlying ledger - one row per symbol per expiry.
  getMasterTrades: async ({ limit = 200, expiry, expiryType } = {}) => {
    const response = await indianClient.get('/performance/master/trades', {
      params: { limit, expiry: expiry || undefined, expiry_type: expiryType || undefined },
      timeout: 60000,
    });
    return response.data;
  },

  // Round-trip ledger. Every row carries `provenance` - keep it visible.
  getPerformanceTrips: async ({ from, to, clientId, limit = 200 } = {}) => {
    const response = await indianClient.get('/performance/trips', {
      params: { from_date: from || undefined, to_date: to || undefined,
                client_id: clientId || undefined, limit },
      timeout: 60000,
    });
    return response.data;
  },

  // Chart annotations: multiplier changes and onboarding.
  getPerformanceEvents: async ({ from, to } = {}) => {
    const response = await indianClient.get('/performance/events', {
      params: { from_date: from || undefined, to_date: to || undefined },
      timeout: 60000,
    });
    return response.data;
  },

  // ============= Infrastructure =============

  // Get infrastructure status (accounts, proxies, health)
  getInfrastructureStatus: async () => {
    const response = await indianClient.get('/infrastructure/status');
    return response.data;
  },

  // Get available (unassigned) proxies for account creation
  getAvailableProxies: async () => {
    const response = await indianClient.get('/proxies/available');
    return response.data;
  },

  // ============= Proxy Management =============

  // Get all proxies with assignment status
  getProxies: async () => {
    const response = await indianClient.get('/proxies');
    return response.data;
  },

  // Sync proxies from provider (server-side)
  syncProxies: async () => {
    const response = await indianClient.post('/proxies/sync');
    return response.data;
  },

  // Assign proxy to account
  assignProxy: async (proxyId, accountId) => {
    const response = await indianClient.patch(`/proxies/${proxyId}/assign`, {
      account_id: accountId
    });
    return response.data;
  },

  // Unassign proxy from account
  unassignProxy: async (proxyId) => {
    const response = await indianClient.patch(`/proxies/${proxyId}/assign`, {
      account_id: null
    });
    return response.data;
  },

  // Migrate existing proxies (one-time)
  migrateProxies: async () => {
    const response = await indianClient.post('/proxies/migrate');
    return response.data;
  },
};

export default indianApi;
